import { useEffect, useState } from 'react'
import { useFocusTrap } from '../lib/focusTrap.js'
import SheetClose from './SheetClose.jsx'

/**
 * The dialog in front of something irreversible (PRIV-001, PLAN §7).
 *
 * `confirmWord` is the difference between this and a browser `confirm()`. For
 * deleting an account, clicking twice is not consent — a person has to type
 * the thing they are about to lose, and a mis-click cannot produce those
 * letters. Everything else about it is the ordinary sheet: Escape closes, Tab
 * cycles inside, focus returns to whatever opened it.
 */
export default function ConfirmDialog({
  open,
  title,
  tone = 'danger',
  confirmLabel = 'Confirm',
  confirmWord = null,
  // A caller's own condition for the confirm, beside `confirmWord`'s: the
  // report dialog holds it until a reason is picked.
  confirmDisabled = false,
  busy = false,
  error = '',
  onConfirm,
  onClose,
  children,
}) {
  const [typed, setTyped] = useState('')
  const dialogRef = useFocusTrap(open, onClose)

  useEffect(() => {
    if (open) setTyped('')
  }, [open])

  if (!open) return null
  const ready = !confirmWord || typed.trim() === confirmWord

  return (
    <div className="sheet-backdrop" onClick={onClose} role="presentation">
      <div
        ref={dialogRef}
        className="sheet confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="sheet-head">
          <div className="row-main">
            <h2 className="title-3 mb-0">{title}</h2>
          </div>
          <SheetClose onClose={onClose} />
        </header>

        <div className="confirm-body">{children}</div>

        {confirmWord ? (
          <label className="field">
            <span className="field-label">
              Type <strong>{confirmWord}</strong> to confirm
            </span>
            <input
              className="input"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          </label>
        ) : null}

        {error ? (
          <p className="form-alert error" role="alert">
            {error}
          </p>
        ) : null}

        <footer className="sheet-foot">
          <button type="button" className="btn btn-quiet" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn ${tone === 'danger' ? 'btn-danger' : 'btn-primary'}`}
            onClick={onConfirm}
            disabled={busy || !ready || confirmDisabled}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  )
}
